(() => {
  if (customElements.get('ob-live-search')) return;
  customElements.define('ob-live-search', class extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.input = this.form.querySelector('[name="q"]');
      this.panel = this.querySelector('.ob-suggest__panel');
      this.status = this.querySelector('[role="status"]');
      this.cache = new Map();
      this.events = new AbortController();
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-controls', this.panel.id);
      this.input.setAttribute('aria-expanded', 'false');
      const on = (target, type, fn) => target.addEventListener(type, fn, { signal: this.events.signal });
      on(this.input, 'input', e => { if (!e.isComposing) this.schedule(); });
      on(this.input, 'compositionstart', () => this.close());
      on(this.input, 'compositionend', () => this.schedule());
      on(this.input, 'focus', () => this.schedule());
      on(this.input, 'keydown', e => this.keydown(e));
      on(this.form, 'submit', () => this.close());
      on(this.form, 'focusout', e => { if (!this.form.contains(e.relatedTarget)) this.close(); });
      on(document, 'pointerdown', e => { if (!this.form.contains(e.target)) this.close(); });
      on(this.panel, 'keydown', e => {
        if (e.key === 'Escape') { e.preventDefault(); this.input.focus(); this.close(); }
      });
      on(window, 'resize', () => { if (!this.form.getClientRects().length) this.close(); else this.size(); });
      if (window.visualViewport) {
        on(window.visualViewport, 'resize', () => this.size());
        on(window.visualViewport, 'scroll', () => this.size());
      }
    }
    disconnectedCallback() { this.close(); this.events.abort(); }
    close() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.request = null;
      this.panel.hidden = true;
      this.panel.removeAttribute('aria-busy');
      this.form.classList.remove('ob-search--loading');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.active = -1;
      this.status.textContent = '';
    }
    size() {
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
      const available = bottom - this.form.getBoundingClientRect().bottom - 20;
      this.style.setProperty('--ob-search-height', Math.max(0, available) + 'px');
    }
    show() {
      this.size();
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }
    schedule() {
      this.close();
      const query = this.input.value.trim();
      if (!query) return;
      if (this.cache.has(query)) { this.search(query); return; }
      this.panel.innerHTML = '<div class="ob-suggest__loading" aria-hidden="true">' +
        '<div class="ob-suggest__loading-label"><span class="ob-suggest__spinner"></span>Finding matches</div>' +
        '<div class="ob-suggest__skeleton"><i></i><span><b></b><b></b></span></div>'.repeat(3) + '</div>';
      this.panel.setAttribute('aria-busy', 'true');
      this.form.classList.add('ob-search--loading');
      this.status.textContent = 'Loading suggestions';
      this.show();
      this.timer = setTimeout(() => this.search(query), 120);
    }
    async search(query) {
      this.request = new AbortController();
      const signal = this.request.signal;
      const timeout = setTimeout(() => this.request === request && request.abort(), 8000);
      const request = this.request;
      try {
        let html = this.cache.get(query);
        if (!html) {
          const url = new URL(this.dataset.url, location.origin);
          url.search = new URLSearchParams({ q: query, 'resources[type]': 'product,query',
            'resources[limit]': '6', 'resources[limit_scope]': 'each',
            section_id: 'ob-predictive-search' }).toString();
          let response = await fetch(url.toString(), { signal });
          // Stores without predictive-search language support still get a small first-page preview.
          if (!response.ok && [404, 417, 422].includes(response.status)) {
            const fallback = new URL(this.form.action);
            fallback.search = new URLSearchParams({ q: query, type: 'product',
              'options[prefix]': 'last', sort_by: 'relevance', section_id: 'ob-predictive-search' }).toString();
            response = await fetch(fallback.toString(), { signal });
          }
          if (!response.ok) throw new Error('Suggestions unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const results = doc.querySelector('[data-ob-results]');
          if (!results) throw new Error('Missing suggestions');
          html = results.innerHTML;
          if (signal.aborted) return;
          if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
          this.cache.set(query, html);
        }
        if (signal.aborted || this.input.value.trim() !== query) return;
        this.panel.innerHTML = html;
        this.panel.querySelectorAll('[role="option"]').forEach((item, index) => { item.id = this.panel.id + '-' + index; });
        this.show();
        const count = this.panel.querySelectorAll('.ob-suggest__item, .ob-suggest__query').length;
        this.status.textContent = count ? count + ' product suggestions available.' : 'No product suggestions found.';
      } catch (error) {
        if (this.request !== request || this.panel.hidden) return;
        this.panel.replaceChildren();
        const message = document.createElement('p');
        message.className = 'ob-suggest__message';
        message.textContent = 'Suggestions are unavailable. Press Search to see results.';
        const link = document.createElement('a');
        const url = new URL(this.form.action);
        url.search = new URLSearchParams({ q: query, type: 'product', 'options[prefix]': 'last', sort_by: 'relevance' }).toString();
        link.href = url.toString();
        link.className = 'ob-suggest__all';
        link.textContent = 'View all results';
        link.setAttribute('role', 'option');
        link.setAttribute('aria-selected', 'false');
        link.id = this.panel.id + '-0';
        this.panel.append(message, link);
        this.status.textContent = message.textContent;
      } finally {
        clearTimeout(timeout);
        if (this.request === request) {
          this.panel.removeAttribute('aria-busy');
          this.form.classList.remove('ob-search--loading');
        }
      }
    }
    keydown(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (this.panel.hidden) {
        if (event.key === 'ArrowDown' && this.input.value.trim()) { event.preventDefault(); this.schedule(); }
        return;
      }
      const items = [...this.panel.querySelectorAll('[role="option"]')];
      if (event.key === 'Enter' && this.active >= 0 && items[this.active]) {
        event.preventDefault(); items[this.active].click(); return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !items.length) return;
      event.preventDefault();
      this.active = this.active < 0 ? (event.key === 'ArrowDown' ? 0 : items.length - 1) : (this.active + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
      items.forEach((item, index) => item.setAttribute('aria-selected', String(index === this.active)));
      this.input.setAttribute('aria-activedescendant', items[this.active].id);
      items[this.active].scrollIntoView({ block: 'nearest' });
    }
  });
})();
